import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { isOverlap } from './GridHelpers';
import { updateCopy, copyItem } from '../../../helpers/copyPaste';
import { KEY_C, KEY_V, KEY_DELETE } from '../../../constants/keyboardFormControl';
import { isInside } from '../../../helpers/formTree';
import { BLOCK } from '../../../constants/draggableType';
import { DEFAULT_GRID_ROWHEIGHT } from '../../../constants/grid';

const CopyPasteGridItem = ({
  formId,
  focusedBlock,
  focusedField,
  blocksBoxes,
  fieldsBoxes,
  allItems,
  gridColumns,
  dragRef,
  getScrollOffset = () => ({ x: 0, y: 0 }),
  addField,
  addBlock,
  updateBlock,
  updateField,
  setFocusedBlock,
  setFocusedField,
  deleteField,
  deleteBlock,
}) => {
  const positionRef = useRef({ x: 0, y: 0 });

  const [shadowBoxCopy, setShadowBoxCopy] = useState(false);
  const [displayPosition, setDisplayPosition] = useState({ x: 0, y: 0 });
  const [itemCopiedData, setItemCopiedData] = useState({});
  const [copiedStatus, setCopiedStatus] = useState('');

  const xDistance = useMemo(() => {
    if (!dragRef?.current?.clientWidth) return 0;
    return (dragRef.current.clientWidth / 12) - 5;
  }, [dragRef?.current?.clientWidth]);

  // Fonction pour calculer si on peut coller à une position donnée
  const canPasteItemHere = (pos) => {
    if (!focusedBlock && !focusedField) return null;

    let itemToCheck;
    let insideBlocks = [];

    if (focusedBlock) {
      itemToCheck = {
        height: focusedBlock.box.height,
        type: BLOCK,
        width: focusedBlock.box.width,
        x: Math.trunc(pos.x / xDistance),
        y: Math.trunc(pos.y / DEFAULT_GRID_ROWHEIGHT),
      };
      insideBlocks = blocksBoxes
        .filter((block) => isInside(focusedBlock.box, block.box) && focusedBlock.id !== block.id)
        .map((block) => block.id);
    } else {
      itemToCheck = {
        height: focusedField.box.height,
        type: focusedField.type,
        width: focusedField.box.width,
        x: Math.trunc(pos.x / xDistance),
        y: Math.trunc(pos.y / DEFAULT_GRID_ROWHEIGHT),
      };
    }
    return !isOverlap(itemToCheck, allItems, { columns: gridColumns }, insideBlocks);
  };

  useEffect(() => {
    const setPositionEvent = (e) => {
      const scrollOffset = getScrollOffset();
      const newPosition = {
        x: e.clientX - 380 + scrollOffset.x,
        y: e.clientY - DEFAULT_GRID_ROWHEIGHT + scrollOffset.y,
      };

      // Toujours mettre à jour le ref (pas de re-render)
      positionRef.current = newPosition;

      // Mettre à jour le state d'affichage uniquement si la shadowBox est visible
      if (shadowBoxCopy && (focusedField || focusedBlock)) {
        setDisplayPosition(newPosition);
      }
    };

    const copyEvent = (e) => {
      const pos = positionRef.current;
      if (pos.x > 0) {
        if (e.ctrlKey && e.key === KEY_C.key) {
          setCopiedStatus('copy');
          setShadowBoxCopy(true);
          setDisplayPosition(pos);
        }
        if (e.ctrlKey && e.key === KEY_V.key) {
          if (focusedField || focusedBlock) {
            copyItem(
              formId,
              canPasteItemHere(pos),
              blocksBoxes,
              fieldsBoxes,
              itemCopiedData,
              focusedBlock,
              focusedField,
              pos,
              xDistance,
              dragRef,
              addField,
              addBlock,
            );
          }
          setCopiedStatus('paste');
          setShadowBoxCopy(true);
          setDisplayPosition(pos);
        }
        if (e.key === KEY_DELETE.key) {
          if (focusedField) deleteField(formId, focusedField.id);
          if (focusedBlock) deleteBlock(formId, focusedBlock.id);
        }
      }
    };

    window.addEventListener('mousemove', setPositionEvent);
    document.addEventListener('keydown', copyEvent);

    return () => {
      window.removeEventListener('mousemove', setPositionEvent);
      document.removeEventListener('keydown', copyEvent);
    };
  }, [
    formId,
    blocksBoxes,
    fieldsBoxes,
    xDistance,
    getScrollOffset,
    addField,
    addBlock,
    deleteField,
    deleteBlock,
    dragRef,
    shadowBoxCopy,
    focusedField,
    focusedBlock,
    itemCopiedData,
    allItems,
    gridColumns,
  ]);

  useEffect(() => {
    if (copiedStatus === 'copy') {
      setItemCopiedData(focusedField || focusedBlock);
      setCopiedStatus('');
    }
    if (copiedStatus === 'paste') {
      updateCopy(
        formId,
        focusedBlock,
        focusedField,
        itemCopiedData,
        blocksBoxes,
        fieldsBoxes,
        updateBlock,
        updateField,
        setFocusedBlock,
        setFocusedField,
      );
      setCopiedStatus('');
    }
  }, [copiedStatus]);

  const handleCloseShadowBox = useCallback(() => {
    setShadowBoxCopy(false);
  }, []);

  if (!shadowBoxCopy) {
    return null;
  }

  const focusedItem = focusedBlock || focusedField;
  if (!focusedItem) {
    return null;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: Math.trunc(displayPosition.y / DEFAULT_GRID_ROWHEIGHT) * DEFAULT_GRID_ROWHEIGHT,
        left: Math.trunc(displayPosition.x / xDistance) * xDistance,
        zIndex: 80000,
        height: focusedItem.box.height * DEFAULT_GRID_ROWHEIGHT,
        width: focusedItem.box.width * xDistance,
        background: canPasteItemHere(displayPosition)
          ? 'rgba(240,240,240,0.5)'
          : 'rgba(221,93,93,0.5)',
        borderRadius: '8px',
      }}
      onClick={handleCloseShadowBox}
    />
  );
};

CopyPasteGridItem.propTypes = {
  formId: PropTypes.string.isRequired,
  focusedBlock: PropTypes.object,
  focusedField: PropTypes.object,
  blocksBoxes: PropTypes.array.isRequired,
  fieldsBoxes: PropTypes.array.isRequired,
  allItems: PropTypes.array.isRequired,
  gridColumns: PropTypes.number.isRequired,
  dragRef: PropTypes.object.isRequired,
  getScrollOffset: PropTypes.func.isRequired,
  addField: PropTypes.func.isRequired,
  addBlock: PropTypes.func.isRequired,
  updateBlock: PropTypes.func.isRequired,
  updateField: PropTypes.func.isRequired,
  setFocusedBlock: PropTypes.func.isRequired,
  setFocusedField: PropTypes.func.isRequired,
  deleteField: PropTypes.func.isRequired,
  deleteBlock: PropTypes.func.isRequired,
};

export default React.memo(CopyPasteGridItem);
